<template>
  <small-modal class="expanded-modal" @close="closeModal">
    <div class="main-wrapper">
      <div class="main-header">
        <h1 class="title">{{ $t("dashboard.topWidgets.title") }}</h1>
      </div>
      <div class="header">
        <p class="subtitle">
          {{ $t("dashboard.topWidgets.subtitle") }}
        </p>
        <p class="subtitle">
          {{ $t("dashboard.topWidgets.numWidgets") }}
          <span>{{ widgets.length }}</span>
        </p>
      </div>
      <div class="video-table-wrapper">
        <div class="video-table-header">
          <div
            class="header-item header-item-name"
            @click="handleHeaderClick('name')"
          >
            <p>{{ $t("dashboard.topWidgets.name") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'name' && selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'name' && selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div class="header-item" @click="handleHeaderClick('type')">
            <p>{{ $t("dashboard.topWidgets.type") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'type' && selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'type' && selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div class="header-item" @click="handleHeaderClick('widgetDisplays')">
            <p>{{ $t("dashboard.topWidgets.widImpressions") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'widgetDisplays' && selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'widgetDisplays' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div class="header-item" @click="handleHeaderClick('widgetClicks')">
            <p>{{ $t("dashboard.topWidgets.widClicks") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'widgetClicks' && selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'widgetClicks' && selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div
            class="header-item"
            @click="handleHeaderClick('widgetClicksPercentage')"
          >
            <p>{{ $t("dashboard.topWidgets.clicksPercentage") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'widgetClicksPercentage' &&
                    selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'widgetClicksPercentage' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div class="header-item" @click="handleHeaderClick('videosPlayed')">
            <p>{{ $t("dashboard.topWidgets.videosWatched") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'videosPlayed' && selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'videosPlayed' && selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div
            class="header-item"
            @click="handleHeaderClick('videosWatchedTime')"
          >
            <p>{{ $t("dashboard.topWidgets.totalTime") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'videosWatchedTime' &&
                    selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'videosWatchedTime' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div
            class="header-item"
            @click="handleHeaderClick('conversionCount')"
          >
            <p>{{ $t("dashboard.topWidgets.orders") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionCount' &&
                    selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionCount' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
          <div
            class="header-item"
            @click="handleHeaderClick('conversionAmount')"
          >
            <p>{{ $t("dashboard.topWidgets.revenue") }}</p>
            <div class="sorting-arrows">
              <SortArrow
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionAmount' &&
                    selectedArrow === 1,
                }"
              />
              <SortArrow
                class="reverse-arrow"
                :class="{
                  'selected-arrow':
                    selectedSorting === 'conversionAmount' &&
                    selectedArrow === -1,
                }"
              />
            </div>
          </div>
        </div>
        <div class="video-table-body">
          <div
            v-for="(widget, index) in sortedWidgets"
            :key="index"
            class="video-table-row"
          >
            <div class="table-cell widget-name">
              {{ widget.name }}
            </div>
            <div
              class="table-cell widget-type"
              :class="{ 'bulk-type': widget.isBulk }"
            >
              <p>
                {{ widget.isBulk ? "Product pages" : "Other pages" }}
              </p>
            </div>
            <div class="table-cell">
              {{ formatNumber(widget.widgetDisplays) }}
            </div>
            <div class="table-cell">
              {{ formatNumber(widget.widgetClicks) }}
            </div>
            <div class="table-cell">
              {{ formatPercentage(widget.widgetClicksPercentage) }}%
            </div>
            <div class="table-cell">
              {{ formatNumber(widget.videosPlayed) }}
            </div>
            <div class="table-cell">
              {{ formatTime(widget.videosWatchedTime) }}
            </div>
            <div class="table-cell">
              {{ formatNumber(widget.conversionCount) }}
            </div>
            <div class="table-cell">
              {{ currency }}{{ formatNumber(widget.conversionAmount) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </small-modal>
</template>

<script>
import SmallModal from "@/components/shared/SmallModal.vue";
import SortArrow from "@/assets/svg/sort-arrow.svg?inline";

export default {
  components: {
    SmallModal,
    SortArrow,
  },
  props: {
    widgets: {
      type: Array,
      default: () => [],
    },
    fromDate: {
      type: Date,
      required: true,
    },
    toDate: {
      type: Date,
      required: true,
    },
    currency: {
      type: String,
      default: "$",
    },
  },
  data() {
    return {
      selectedSorting: "widgetDisplays",
      selectedArrow: -1,
    };
  },
  computed: {
    sortedWidgets() {
      const sortKey = this.selectedSorting;
      const arrow = this.selectedArrow;

      return [...this.widgets].sort((a, b) => {
        let valA = a[sortKey];
        let valB = b[sortKey];

        // Handle type separately
        if (sortKey === "type") {
          valA = a.isBulk ? "Product pages" : "Other pages";
          valB = b.isBulk ? "Product pages" : "Other pages";
        }

        // Handle string sorting (name and type)
        if (sortKey === "name" || sortKey === "type") {
          valA = valA?.toString().toLowerCase() || "";
          valB = valB?.toString().toLowerCase() || "";
          return valA.localeCompare(valB) * arrow;
        }

        // Handle numeric values
        valA = isNaN(valA) ? 0 : Number(valA);
        valB = isNaN(valB) ? 0 : Number(valB);
        return (valA - valB) * arrow;
      });
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    handleHeaderClick(sortKey) {
      if (this.selectedSorting === sortKey) {
        this.selectedArrow *= -1;
      } else {
        this.selectedSorting = sortKey;
        this.selectedArrow = 1;
      }
    },
    formatTime(seconds) {
      if (seconds === null || seconds === undefined || isNaN(seconds)) {
        return "0min";
      }
      const totalSeconds = Number(seconds);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      if (hours > 0) {
        return `${hours}h ${minutes}min`;
      }
      return `${minutes}min`;
    },
    formatPercentage(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return "0.00";
      }
      return Number(value).toFixed(2);
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return "0";
      }
      const num = Number(value);
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style lang="scss">
.expanded-modal .small-modal.bigger {
  width: 90vw;
  max-width: 90vw;
  height: 90vh;
  max-height: 90vh;
  overflow: auto;
}
</style>

<style lang="scss" scoped>
.main-wrapper {
  padding: 32px;
}

.title {
  @include base-font;
  @include font-big;
  font-weight: 600;
}
.subtitle {
  @include base-font;
  @include font-normal;
  span {
    font-weight: 600;
  }
}

.video-table-header {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  background-color: #ececec;
}

.header-item {
  padding: 12px 16px;
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  p {
    margin: 0;
  }
}
.header-item-name {
  text-align: left;
  justify-content: flex-start;
}

.sorting-arrows {
  display: flex;
  flex-direction: column;
  gap: 6px;
  transform: scale(60%);
}

.selected-arrow {
  path {
    fill: $light-purple;
  }
}

.reverse-arrow {
  transform: translateX(-0.9px);
  path {
    transform: rotate(180deg);
    transform-origin: 50% 50%;
  }
}

.video-table-row {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  @include base-font;
  @include font-smaller;
  border-bottom: 1px solid $light-grey;

  &:hover {
    background-color: #f6f6f6;
  }
}

.table-cell {
  padding: 12px 16px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  &:last-child {
    border-right: none;
  }
}

.widget-name {
  text-align: left;
  justify-content: flex-start;
}

.widget-type {
  p {
    background-color: #0030d333;
    padding: 4px 12px;
    border-radius: 40px;
    white-space: nowrap;
  }
}

.bulk-type {
  p {
    background-color: #A328C133;
  }
}

.flex-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 71px;
}

.name-date {
  display: flex;
  flex-direction: column;
  gap: 8px;
  white-space: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  span {
    color: #7f7f7f;
  }
}
.main-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.export-button {
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  height: 30px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid $light-grey;
  color: $light-grey;
  background: white;
  border-radius: 3px;

  &:hover {
    border: 1px solid $dark-purple;
    background: $light-pink;
  }
}
</style>
